import React from "react";
import { Link } from "react-router-dom";
import {
  Globe,
  UploadCloud,
  Video,
  Stamp,
  Wifi,
  Smartphone,
  Camera,
  ArrowUpRight,
} from "lucide-react";
import { PageHero, GoldButton, SectionEyebrow, FadedBackdrop } from "@/components/ui-bits";
import { IMG } from "@/constants/images";

const RON_IMG =
  "https://images.pexels.com/photos/574086/pexels-photo-574086.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const STEPS = [
  {
    icon: UploadCloud,
    step: "01",
    title: "Upload Document & Verify ID",
    body: "Upload your unsigned PDF and complete identity verification — knowledge-based authentication (KBA) plus credential analysis of your government-issued photo ID.",
  },
  {
    icon: Video,
    step: "02",
    title: "Join Secure Video Room",
    body: "Connect by webcam from anywhere in the world. The notary verifies your identity on camera, witnesses your signature, and the entire session is securely recorded for the legal audit trail.",
  },
  {
    icon: Stamp,
    step: "03",
    title: "Digitally Seal Document",
    body: "The notary applies a tamper-evident digital seal and electronic signature. You receive the fully executed document and a copy of the journal entry — usually within minutes.",
  },
];

const REQUIREMENTS = [
  {
    icon: Camera,
    title: "Webcam + Microphone",
    body: "A working webcam and microphone on a desktop, laptop, tablet, or modern smartphone.",
  },
  {
    icon: Smartphone,
    title: "Smartphone + Photo ID",
    body: "A smartphone to capture front/back photos of your unexpired, government-issued photo ID for credential analysis.",
  },
  {
    icon: Wifi,
    title: "Steady Internet Connection",
    body: "A stable broadband or LTE/5G connection capable of supporting two-way video. Avoid public/open Wi-Fi when possible.",
  },
];

export default function RONPage() {
  return (
    <>
      <PageHero
        eyebrow="Remote Online Notary"
        title="Sign from anywhere in the world. Securely, lawfully, and in minutes."
        lead="OGO Notary performs Remote Online Notarization (RON) under our Florida commission using state-approved audio-video and identity-verification technology — backed by a secure, encrypted, and fully audited digital workflow."
        backdrop={IMG.remoteLaptop}
        backdropOpacity={0.6}
        bright
      >
        <div className="flex flex-wrap gap-4">
          <GoldButton as={Link} to="/contact" data-testid="ron-book-btn">
            Start a RON Session <ArrowUpRight className="w-4 h-4" />
          </GoldButton>
        </div>
      </PageHero>

      {/* Absolute Rule callout */}
      <section className="border-b border-white/10 bg-[#1A2B45]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-2 flex lg:justify-center">
            <div className="w-16 h-16 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
              <Globe className="w-7 h-7" />
            </div>
          </div>
          <div className="lg:col-span-10">
            <SectionEyebrow className="mb-3">The Absolute Rule</SectionEyebrow>
            <p className="font-serif-display text-3xl sm:text-4xl text-white leading-tight">
              "The signer can be anywhere in the world, backed by secure,
              state-of-the-art identity verification via{" "}
              <span className="text-[#D4AF37]">OGONotary.com</span>."
            </p>
          </div>
        </div>
      </section>

      {/* 3-step workflow */}
      <section className="relative overflow-hidden py-24 sm:py-32 border-b border-white/10">
        <FadedBackdrop src={IMG.remoteLaptop} opacity={0.2} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mb-14">
            <SectionEyebrow className="mb-4">How RON Works</SectionEyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl text-white tracking-tight leading-tight">
              Three steps from upload to digitally sealed.
            </h2>
          </div>

          <ol className="grid md:grid-cols-3 gap-6 lg:gap-8 relative">
            {STEPS.map((s, i) => (
              <li
                key={i}
                data-testid={`ron-step-${i + 1}`}
                className="relative border border-white/10 bg-[#1A2B45] p-8 hover:border-[#D4AF37]/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37]">
                    <s.icon className="w-6 h-6" />
                  </div>
                  <span className="font-serif-display text-4xl text-[#D4AF37]/40">
                    {s.step}
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl text-white mb-3">
                  {s.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Image + requirements */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div
              className="relative border border-white/10 aspect-[4/5] bg-cover bg-center"
              style={{ backgroundImage: `url(${RON_IMG})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-[#142339] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/10 bg-[#142339]/80 backdrop-blur-sm">
                <div className="eyebrow mb-2">Secure by Design</div>
                <p className="font-serif-display text-xl text-white">
                  Encrypted video. Tamper-evident seal. Full audit trail.
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <SectionEyebrow className="mb-4">What You'll Need</SectionEyebrow>
            <h2 className="font-serif-display text-4xl text-white leading-tight">
              Technical requirements for your session.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed">
              We'll send a confirmation email with a secure link. Before the
              session, please make sure you have:
            </p>
            <div className="mt-8 space-y-4">
              {REQUIREMENTS.map((r, i) => (
                <div
                  key={i}
                  data-testid={`ron-req-${i}`}
                  className="flex items-start gap-4 border border-white/10 bg-[#1A2B45] p-6"
                >
                  <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0">
                    <r.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-serif-display text-xl text-white mb-1">
                      {r.title}
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      {r.body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-slate-500 text-xs leading-relaxed mt-8">
              Note: Some documents and jurisdictions are not eligible for RON
              (for example, certain wills and codicils in some states). We'll
              confirm eligibility before your session.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
